class DSCartDiscountComponent extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('[data-cart-discount-form]');
    this.input = this.querySelector('[data-cart-discount-input]');
    this.errorContainer = this.querySelector('[data-cart-discount-error]');
    this.errorText = this.querySelector('[data-cart-discount-error-text]');
    this.codesContainer = this.querySelector('.ds-cart-discount__codes');
    this.toggleButton = this.querySelector('[data-discount-toggle]');
    this.expandableContent = this.querySelector('[data-discount-expandable]');
    this.abortController = null;
    this.isOpen = false;

    this.onCartUpdated = this.handleCartUpdate.bind(this);
  }

  connectedCallback() {
    this.init();
    document.addEventListener('liquid-ajax-cart:request-end', this.onCartUpdated);
  }

  disconnectedCallback() {
    document.removeEventListener('liquid-ajax-cart:request-end', this.onCartUpdated);
  }

  init() {
    if (this.toggleButton) {
      this.toggleButton.addEventListener('click', () => this.toggleDropdown());
    }

    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit);
      this.handleSubmit = (e) => {
        e.preventDefault();
        this.applyDiscount();
      };
      this.form.addEventListener('submit', this.handleSubmit);
    }

    if (this.input) {
      this.input.addEventListener('input', () => this.hideError());
    }

    this.addEventListener('click', (e) => {
      const removeButton = e.target.closest('[data-cart-discount-remove]');
      if (removeButton) {
        this.removeDiscount(e, removeButton);
      }

      const closeErrorButton = e.target.closest('[data-cart-discount-error-close]');
      if (closeErrorButton) {
        this.hideError();
      }
    });
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;

    if (this.toggleButton) {
      this.toggleButton.setAttribute('aria-expanded', this.isOpen.toString());
    }

    if (this.expandableContent) {
      if (this.isOpen) {
        this.expandableContent.classList.add('is-open');
      } else {
        this.expandableContent.classList.remove('is-open');
      }
    }
  }

  handleCartUpdate(event) {
    const { cart } = window.liquidAjaxCart;
    const currentCodes = this.getDiscountCodesFromCart(cart);
    const displayedCodes = this.getExistingDiscounts();

    const addedCodes = currentCodes.filter(code => !displayedCodes.includes(code));
    const removedCodes = displayedCodes.filter(code => !currentCodes.includes(code));

    if (addedCodes.length === 0 && removedCodes.length === 0) return;

    removedCodes.forEach(code => {
      const pill = this.querySelector(`.ds-cart-discount__pill[data-discount-code="${code}"]`);
      if (pill) pill.remove();
    });

    if (addedCodes.length > 0) {
      const oldHeight = this.codesContainer ? this.codesContainer.offsetHeight : 0;

      const newPills = addedCodes.map(code => {
        const pill = this.createPillElement(code);
        pill.style.opacity = '0';
        this.codesContainer.appendChild(pill);
        return pill;
      });

      if (this.codesContainer) {
        const newHeight = this.codesContainer.scrollHeight;

        if (Math.abs(oldHeight - newHeight) > 1) {
          this.codesContainer.style.height = `${oldHeight}px`;
          this.codesContainer.style.overflow = 'hidden';
          this.codesContainer.style.transition = 'height 0.3s ease-out';

          this.codesContainer.offsetHeight;

          this.codesContainer.style.height = `${newHeight}px`;

          let transitionFinished = false;
          const transitionTimeout = setTimeout(() => {
            if (!transitionFinished) {
              transitionFinished = true;
              this.codesContainer.removeEventListener('transitionend', onTransitionEnd);
              this.cleanupAnimation(newPills);
            }
          }, 400);

          const onTransitionEnd = (e) => {
            if (e.target !== this.codesContainer || e.propertyName !== 'height') return;
            if (!transitionFinished) {
              transitionFinished = true;
              clearTimeout(transitionTimeout);
              this.codesContainer.removeEventListener('transitionend', onTransitionEnd);
              this.cleanupAnimation(newPills);
            }
          };

          this.codesContainer.addEventListener('transitionend', onTransitionEnd);
        } else {
          this.animatePills(newPills);
        }
      } else {
        this.animatePills(newPills);
      }
    }
  }

  createPillElement(code) {
    const li = document.createElement('li');
    li.className = 'ds-cart-discount__pill';
    li.setAttribute('data-discount-code', code);
    li.setAttribute('aria-label', window.cartTranslations?.ds_cart?.accessibility?.discount_applied?.replace('{{code}}', code) || `Discount ${code} applied`);

    li.innerHTML = `
      <div class="ds-cart-discount__pill-icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false" width="12" height="12">
          <path d="M2 6L4.5 8.5L10 3.5" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <p class="ds-cart-discount__pill-code">${code}</p>
      <button type="button" class="ds-cart-discount__pill-remove button-unstyled" aria-label="${window.cartTranslations?.ds_cart?.accessibility?.remove_discount?.replace('{{code}}', code) || `Remove ${code}`}" data-cart-discount-remove data-discount-code="${code}">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 256 256">
          <path fill-rule="even-odd" fill="currentColor" d="M165.66 101.66 139.31 128l26.35 26.34a8 8 0 0 1-11.32 11.32L128 139.31l-26.34 26.35a8 8 0 0 1-11.32-11.32L116.69 128l-26.35-26.34a8 8 0 0 1 11.32-11.32L128 116.69l26.34-26.35a8 8 0 0 1 11.32 11.32ZM232 128A104 104 0 1 1 128 24a104.11 104.11 0 0 1 104 104Zm-16 0a88 88 0 1 0-88 88 88.1 88.1 0 0 0 88-88Z"></path>
        </svg>
      </button>
    `;

    return li;
  }

  cleanupAnimation(newPills) {
    if (this.codesContainer) {
      this.codesContainer.style.height = 'auto';
      this.codesContainer.style.overflow = '';
      this.codesContainer.style.transition = '';
    }
    this.animatePills(newPills);
  }

  animatePills(pills) {
    pills.forEach((pill, index) => {
      pill.animate([
        { opacity: 0, transform: 'translateX(-10px)' },
        { opacity: 1, transform: 'none' }
      ], {
        duration: 300,
        easing: 'ease-out',
        fill: 'forwards',
        delay: index * 50
      });
    });
  }

  getDiscountCodesFromCart(cart) {
    if (!cart || !cart.discount_codes) return [];
    return cart.discount_codes
      .filter(discount => discount.applicable)
      .map(discount => discount.code);
  }

  getExistingDiscounts() {
    const pills = this.querySelectorAll('.ds-cart-discount__pill');
    return Array.from(pills).map(pill => pill.dataset.discountCode).filter(Boolean);
  }

  async applyDiscount() {
    const discountCode = this.input.value.trim();

    if (!discountCode) {
      this.showError(window.cartTranslations?.ds_cart?.discount?.error_empty || 'Bitte einen Rabattcode eingeben.');
      return;
    }

    this.hideError();

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    try {
      const existingDiscounts = this.getExistingDiscounts();

      if (existingDiscounts.includes(discountCode)) {
        this.showError(window.cartTranslations?.ds_cart?.discount?.error_already_applied || 'Dieser Code wurde bereits angewendet.');
        return;
      }

      const allDiscounts = [discountCode];

      const options = {
        lastCallback: (requestState) => {
          const appliedDiscountCodes = requestState.responseData.body.discount_codes || [];
          const wasApplied = appliedDiscountCodes.some(
            discountObj => discountObj.code.toLowerCase() === discountCode.toLowerCase() && discountObj.applicable
          );
          if (!wasApplied) {
            if (this.isCampaignCode(discountCode)) {
              this.input.value = '';
            } else {
              const msg = (window.cartTranslations?.ds_cart?.discount?.error_could_not_apply || 'Code {{code}} konnte nicht angewendet werden.').replace('{{code}}', discountCode);
              this.showError(msg);
            }
          } else {
            this.input.value = '';
          }
        }
      };

      await window.liquidAjaxCart.update({
        discount: allDiscounts.join(','),
        attributes: { active_code: discountCode }
      }, options);

    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Discount application error:', error);
      this.showError(window.cartTranslations?.ds_cart?.discount?.error_generic || 'Ein Fehler ist aufgetreten.');
    }
  }

  async removeDiscount(event, button) {
    const discountCode = button.dataset.discountCode;
    if (!discountCode) return;
    if (button.disabled) return;

    button.disabled = true;
    this.hideError();

    const pill = button.closest('.ds-cart-discount__pill');
    if (pill && window.DSCartAnimations) {
      window.DSCartAnimations.fade(pill, 'out');
    }

    try {
      const existingDiscounts = this.getExistingDiscounts();
      const remainingDiscounts = existingDiscounts.filter(code => code !== discountCode);

      await window.liquidAjaxCart.update({
        discount: remainingDiscounts.length > 0 ? remainingDiscounts.join(',') : '',
        attributes: { active_code: '' }
      });
      button.disabled = false;
    } catch (error) {
      console.error('Discount removal error:', error);
      this.showError(window.cartTranslations?.ds_cart?.discount?.error_remove || 'Code konnte nicht entfernt werden.');
      button.disabled = false;
      if (pill) {
        pill.style.opacity = '';
        pill.style.transform = '';
        pill.style.height = '';
        pill.style.margin = '';
        pill.style.padding = '';
      }
    }
  }

  isCampaignCode(code) {
    try {
      const el = document.getElementById('ds-cart-campaigns');
      if (!el) return false;
      const campaignsJson = JSON.parse(el.textContent);
      const campaigns = JSON.parse(atob(campaignsJson.campaigns));
      return campaigns.some(camp => camp.code?.toLowerCase() === code.toLowerCase());
    } catch (e) {
      return false;
    }
  }

  showError(message) {
    if (this.errorContainer && this.errorText) {
      this.errorText.textContent = message;
      this.errorContainer.classList.remove('hidden');
      if (window.DSCartAnimations) {
        window.DSCartAnimations.slideInError(this.errorContainer);
      }
    }
  }

  hideError() {
    if (this.errorContainer) {
      this.errorContainer.classList.add('hidden');
      if (this.errorText) {
        this.errorText.textContent = '';
      }
    }
  }
}

if (!customElements.get('ds-cart-discount-component')) {
  customElements.define('ds-cart-discount-component', DSCartDiscountComponent);
}
